import { Box, Chip, Dialog, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography, CircularProgress, Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, ShoppingCart as CartIcon } from '@mui/icons-material';
import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { feedback, IServicePackageView, servicePackageService } from '../../../shared/services';
import { IPropsBusinessPage } from '../Business';
import { Button } from '../../../shared/components/MUI/button/Button';
import { DeleteConfirmationDialog } from '../../../shared/components/SconApp/deleteConfirmation/DeleteConfirmationDialog';
import { BusinessPackagesRegister } from './BusinessPackage';
import { SellPackageModal } from './SellPackageModal';

const truncate = (text: string, max = 25): string =>
  text.length > max ? `${text.slice(0, max)}...` : text;

export const BusinessPackagesManage: React.FC<IPropsBusinessPage> = ({ business, refetch }) => {
  const [openRegisterDialog, setOpenRegisterDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSellDialog, setOpenSellDialog] = useState(false);
  const [selectedId, setSelectedId] = useState<number>();
  const [selectedPkg, setSelectedPkg] = useState<IServicePackageView>();
  const queryClient = useQueryClient();

  const { data: packages, isFetching } = useQuery({
    queryKey: ['servicePackages', business.id],
    queryFn: () => servicePackageService.getByBusiness(business.id),
  });

  const { mutate: remove, isLoading: isLoadingDelete } = useMutation(
    (id: number) => servicePackageService.remove(id),
    {
      onSuccess: () => {
        refetch();
        handleClose();
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const onClickEdit = useCallback((id: number) => {
    setSelectedId(id);
    setOpenRegisterDialog(true);
  }, []);

  const onClickDelete = useCallback((id: number) => {
    setSelectedId(id);
    setOpenDeleteDialog(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpenRegisterDialog(false);
    setOpenDeleteDialog(false);
    setOpenSellDialog(false);
    setSelectedId(undefined);
    setSelectedPkg(undefined);
    queryClient.invalidateQueries({ queryKey: ['servicePackages', business.id] });
  }, [business.id, queryClient]);

  const onClickSell = useCallback((pkg: IServicePackageView) => {
    setSelectedPkg(pkg);
    setOpenSellDialog(true);
  }, []);

  return (
    <Box display='flex' flexDirection='column' gap={3}>
      <Box display='flex' justifyContent='space-between'>
        <Typography variant='h6'>Pacotes de Serviços</Typography>
        <Button
          variant='contained'
          onClick={() => { setSelectedId(undefined); setOpenRegisterDialog(true); }}
        >
          Novo pacote
        </Button>
      </Box>

      {isFetching ? (
        <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
      ) : packages && packages.length > 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell width={100}>Ações</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Comissão</TableCell>
                <TableCell>Usos</TableCell>
                <TableCell>Serviço</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map(pkg => (
                <TableRow key={pkg.id}>
                  <TableCell>
                    <Box display='flex' gap={0.5}>
                      <Tooltip title="Vender este pacote para um cliente">
                        <IconButton size='small' onClick={() => onClickSell(pkg)} disabled={!pkg.active}>
                          <CartIcon fontSize='small' color='primary' />
                        </IconButton>
                      </Tooltip>
                      <IconButton size='small' onClick={() => onClickEdit(pkg.id)}><EditIcon fontSize='small' /></IconButton>
                      <IconButton size='small' onClick={() => onClickDelete(pkg.id)}><DeleteIcon fontSize='small' /></IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' fontWeight={500}>{truncate(pkg.name)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' color='text.secondary'>{pkg.description ? truncate(pkg.description) : '—'}</Typography>
                  </TableCell>
                  <TableCell>R$ {pkg.amount.toFixed(2)}</TableCell>
                  <TableCell>{pkg.commissionPercent != null ? `${pkg.commissionPercent}%` : '—'}</TableCell>
                  <TableCell>{pkg.totalUses}</TableCell>
                  <TableCell>
                    <Chip label={truncate(pkg.serviceName)} size='small' variant='outlined' />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pkg.active ? 'Ativo' : 'Inativo'}
                      size='small'
                      color={pkg.active ? 'success' : 'default'}
                      variant='outlined'
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box display='flex' justifyContent='center' py={6}>
          <Typography color='text.secondary'>Nenhum pacote cadastrado.</Typography>
        </Box>
      )}

      <Dialog open={openRegisterDialog} onClose={handleClose} maxWidth='md' fullWidth>
        <BusinessPackagesRegister
          businessId={business.id}
          packageId={selectedId}
          refetch={refetch}
          onCloseEdit={handleClose}
        />
      </Dialog>

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={handleClose}
        onConfirm={() => selectedId && remove(selectedId)}
        isLoading={isLoadingDelete}
      />

      {selectedPkg && (
        <SellPackageModal
          open={openSellDialog}
          onClose={handleClose}
          pkg={selectedPkg}
          businessId={business.id}
        />
      )}
    </Box>
  );
};
